// filepath: /Users/bipul/wspace/ai11_base/ai11_admin/src/pages/Connect/TopicForm.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../../services/api';
import { 
  useGetNotificationTopicQuery, 
  useCreateNotificationTopicMutation, 
  useUpdateNotificationTopicMutation 
} from '../../services/connectApi';
import type { App } from '../../models';

interface NotificationTopicFormData {
  name: string;
  firebase_topic: string;
  description: string;
  app: string;
  is_public: boolean;
  subscriber_count: number;
}

const TopicForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const [apps, setApps] = useState<App[]>([]);
  
  // Use Redux hooks for API operations
  const { data: topicData, isLoading: isLoadingTopic } = useGetNotificationTopicQuery(id || '', {
    skip: !isEditMode || id === 'new'
  });
  
  const [createTopic, { isLoading: isCreating }] = useCreateNotificationTopicMutation();
  const [updateTopic, { isLoading: isUpdating }] = useUpdateNotificationTopicMutation();
  
  const loading = isEditMode && isLoadingTopic;
  const saving = isCreating || isUpdating;
  
  const validationSchema = Yup.object({
    name: Yup.string().required('Topic name is required'),
    firebase_topic: Yup.string()
      .required('Firebase topic ID is required')
      .matches(/^[A-Za-z0-9_-]+$/, 'Only alphanumeric characters, hyphens, and underscores are allowed'),
    app: Yup.string().required('App is required')
  });
  
  const formik = useFormik<NotificationTopicFormData>({
    initialValues: {
      name: '',
      firebase_topic: '',
      description: '',
      app: '',
      is_public: false,
      subscriber_count: 0
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (isEditMode && id) {
          await updateTopic({ id, data: values });
        } else {
          await createTopic(values);
        }
        navigate('/connect#topics');
      } catch (error) {
        console.error('Error saving topic:', error);
      }
    }
  });
  
  useEffect(() => {
    const fetchApps = async () => {
      try {
        const response = await api.get('/app/apps/');
        setApps(response.data || []);
      } catch (error) {
        console.error('Error fetching apps:', error);
      }
    };
    
    fetchApps();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Set form values when topic data is loaded
  useEffect(() => {
    if (topicData && isEditMode) {
      formik.setValues({
        name: topicData.name || '',
        firebase_topic: topicData.firebase_topic || '',
        description: topicData.description || '',
        app: topicData.app || '',
        is_public: topicData.is_public || false,
        subscriber_count: topicData.subscriber_count || 0
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicData, isEditMode]);
  
  const handleAppChange = (app: App | null) => {
    // setSelectedApp(app);
    formik.setFieldValue('app', app?.id || '');
  };
  
  const generateFirebaseTopic = () => {
    if (formik.values.name && !formik.values.firebase_topic) {
      const formatted = formik.values.name
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, '_')
        .replace(/_+/g, '_');
      
      formik.setFieldValue('firebase_topic', formatted);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/connect#topics')}
        >
          Back to Topics
        </Button>
      </Box>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {isEditMode ? 'Edit Topic' : 'Create New Topic'}
        </Typography>
        
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Topic Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  generateFirebaseTopic();
                }}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={
                  formik.touched.name && formik.errors.name
                    ? formik.errors.name
                    : 'A descriptive name for this topic'
                }
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="firebase_topic"
                name="firebase_topic"
                label="Firebase Topic ID"
                value={formik.values.firebase_topic}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.firebase_topic && Boolean(formik.errors.firebase_topic)}
                helperText={
                  formik.touched.firebase_topic && formik.errors.firebase_topic
                    ? formik.errors.firebase_topic
                    : 'Unique identifier used in Firebase for this topic (alphanumeric, hyphens, and underscores)'
                }
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="description"
                name="description"
                label="Description"
                multiline
                rows={3}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={
                  formik.touched.description && formik.errors.description
                    ? formik.errors.description
                    : 'Optional description of the topic purpose'
                }
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="is_public"
                    checked={formik.values.is_public}
                    onChange={formik.handleChange}
                  />
                }
                label="Public Topic (Any user can subscribe)"
              />
              <FormHelperText>
                If checked, any user can subscribe to this topic. Otherwise, subscriptions must be managed by the app.
              </FormHelperText>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth error={formik.touched.app && Boolean(formik.errors.app)}>
                <InputLabel id="app-label">App</InputLabel>
                <Select
                  labelId="app-label"
                  id="app"
                  name="app"
                  value={formik.values.app}
                  onChange={(e) => {
                    const appId = e.target.value;
                    const app = apps.find((a) => a.id === appId) || null;
                    handleAppChange(app);
                  }}
                  onBlur={formik.handleBlur}
                  required
                >
                  {apps.map((app) => (
                    <MenuItem key={app.id} value={app.id}>
                      {app.name} ({app.type})
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.app && formik.errors.app && (
                  <FormHelperText>{formik.errors.app}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            {isEditMode && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="subscriber_count"
                  name="subscriber_count"
                  label="Subscriber Count"
                  type="number"
                  value={formik.values.subscriber_count}
                  onChange={formik.handleChange}
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="Current number of subscribers to this topic"
                />
              </Grid>
            )}
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate('/connect#topics')}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={saving || !formik.isValid || !formik.dirty}
            >
              {isEditMode ? 'Update Topic' : 'Create Topic'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default TopicForm;
